import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { SheetRequest } from '../hooks/gestures/useSheetTriggers'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { COARSE_POINTER_QUERY, MOBILE_QUERY, TOUCH_SCREEN_QUERY } from '../lib/platform'
import { FOCUSABLE, moveFocus, trapTab } from './focus'
import { Icon, type IconName } from './Icon'
import { anchoredPlacement } from './sheetPlacement'

/** What a lifted sheet hangs from: the message on screen, and which edge of it the actions line up with. */
export interface SheetAnchor {
  element: HTMLElement | null
  side: 'own' | 'other'
}

interface Props<View extends string> {
  /** The dialog's name, e.g. "Message actions". */
  label: string
  request: SheetRequest
  onClose: () => void
  /** Given on a touch screen or a phone-sized window, the sheet lifts this element in place instead of sliding up. */
  anchor?: SheetAnchor | null
  /** Which part of the sheet shows, for sheets with more than one (the actions, then the emoji picker). */
  view?: View
  /** What to focus when a view shows, given the one shown before (none at first). By default the first action. */
  focusIn?: (panel: HTMLElement, previousView: View | undefined) => HTMLElement | null
  children: ReactNode
}

/** The strips a phone keeps for the clock (and the blur under it) and the home indicator, as base.css publishes them. */
function safeAreas(): { top: number; bottom: number } {
  const style = getComputedStyle(document.documentElement)
  const read = (name: string) => Number.parseFloat(style.getPropertyValue(name)) || 0
  return { top: read('--safe-top') + read('--top-glass'), bottom: read('--safe-bottom') }
}

/**
 * Actions for a message or a conversation (spec §3.3, §3.6): the held message lifted in place where it is given an
 * anchor, a bottom sheet on phones otherwise, or a menu at the pointer. Modal: focus stays inside until it closes.
 */
export function ActionSheet<View extends string>({ label, request, onClose, anchor, view, focusIn, children }: Props<View>) {
  const phone = useMediaQuery(MOBILE_QUERY)
  const coarse = useMediaQuery(COARSE_POINTER_QUERY)
  const touchScreen = useMediaQuery(TOUCH_SCREEN_QUERY)
  // Where a menu at the pointer will not do — a touch screen, or a window as narrow as a phone — the message stays
  // put and its actions come to it.
  const lifted = (touchScreen || phone) && Boolean(anchor?.element)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const shownView = useRef<View | undefined>(undefined)
  const { at, returnFocus } = request

  useLayoutEffect(() => {
    const dialog = dialogRef.current!
    if (typeof dialog.showModal === 'function') dialog.showModal()
    else dialog.setAttribute('open', '')
    return () => {
      // Close first: nothing outside a modal dialog can take focus.
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
      returnFocus?.focus({ preventScroll: true })
    }
  }, [returnFocus])

  // Menus open at the point asked for, kept on screen (above it when there is no room below), and are placed again
  // when their size changes: another view, or an action added once its rule has loaded.
  useLayoutEffect(() => {
    const panel = panelRef.current!
    if (phone && !lifted) {
      panel.style.left = panel.style.top = ''
      return
    }
    const place = () => {
      // The laid-out size, not the drawn one: the panel grows into place from 96%, and a scaled box would misplace it.
      const { offsetWidth: width, offsetHeight: height } = panel
      if (lifted) {
        // The copy of the message lands over the message it came from; the group then moves to stay on screen.
        const copy = panel.querySelector<HTMLElement>('.sheet-message')
        const { left, top } = anchoredPlacement({
          rect: anchor!.element!.getBoundingClientRect(),
          panel: { width, height, copyTop: copy?.offsetTop ?? 0, copyHeight: copy?.offsetHeight ?? 0 },
          viewport: { width: window.innerWidth, height: window.innerHeight },
          side: anchor!.side,
          safe: safeAreas(),
        })
        panel.style.left = `${left}px`
        panel.style.top = `${top}px`
        return
      }
      const margin = 8
      const left = Math.max(margin, Math.min(at.x, window.innerWidth - width - margin))
      const top = at.y + height <= window.innerHeight - margin ? at.y : Math.max(margin, at.y - height)
      panel.style.left = `${left}px`
      panel.style.top = `${top}px`
    }
    place()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(place)
    observer.observe(panel)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the anchor element is read when the sheet is placed
  }, [phone, lifted, at, view])

  useLayoutEffect(() => {
    const panel = panelRef.current!
    const target = focusIn?.(panel, shownView.current) ?? panel.querySelector<HTMLElement>('[role="menuitem"]') ?? panel.querySelector<HTMLElement>(FOCUSABLE)
    if (target) {
      // No focus ring where a finger or pen opened the sheet; a key press brings it back. Opening the dialog may have
      // focused the target with a ring already, so focus it afresh. (focusVisible is not in TypeScript's FocusOptions
      // yet; browsers without it show the ring as before.)
      if (request.touch && target === document.activeElement) target.blur()
      target.focus(request.touch ? ({ focusVisible: false } as FocusOptions) : undefined)
    }
    shownView.current = view
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when another view shows
  }, [view])

  return createPortal(
    <dialog
      ref={dialogRef}
      className={`action-sheet ${lifted ? 'is-anchored' : phone ? 'is-sheet' : 'is-popover'}`}
      aria-label={label}
      data-no-gestures
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onClose()
          return
        }
        trapTab(event, panelRef.current)
      }}
      onMouseDown={(event) => {
        // The dialog fills the screen around the panel: a press on it is outside. (Not a click, which a drag from
        // inside the panel, e.g. selecting search text, ends with too.)
        if (event.target === event.currentTarget) onClose()
      }}
      onContextMenu={(event) => {
        if (!(event.target as Element).closest('input')) event.preventDefault()
      }}
    >
      <div className="sheet-panel" ref={panelRef} data-side={lifted ? anchor!.side : undefined}>
        {children}
        {/* A lifted sheet needs no Cancel: the dimmed conversation around it is one big target to tap. */}
        {!lifted && (phone || coarse || request.touch) ? (
          <button type="button" className="sheet-cancel" onClick={onClose}>
            Cancel
          </button>
        ) : null}
      </div>
    </dialog>,
    document.body,
  )
}

export interface SheetMenuItem {
  id: string
  label: string
  icon?: IconName
  danger?: boolean
  /** Shows more choices in the sheet (e.g. how long to mute for) instead of closing it. */
  more?: boolean
  /** The state the action acts on, said on the item itself (e.g. "until 14:30"), and read with its name. */
  detail?: string
  run: () => void
}

interface SheetMenuProps {
  label: string
  items: SheetMenuItem[]
  /** More actions may still be added, once a rule has loaded. */
  busy?: boolean
  onClose: () => void
}

/**
 * A sheet's actions, moved through with the arrow keys. Choosing one closes the sheet, then runs it; an action with more
 * choices opens them in the sheet, with Enter or →.
 */
export function SheetMenu({ label, items, busy, onClose }: SheetMenuProps) {
  return (
    <div
      className="sheet-actions"
      role="menu"
      aria-label={label}
      aria-busy={busy || undefined}
      onKeyDown={(event) => {
        const item = items.find((i) => i.id === (event.target as HTMLElement).dataset.item)
        if (event.key === 'ArrowRight' && item?.more) {
          event.preventDefault()
          item.run()
          return
        }
        moveFocus(event, [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')], { next: 'ArrowDown', previous: 'ArrowUp' })
      }}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          data-item={item.id}
          aria-haspopup={item.more ? 'menu' : undefined}
          aria-label={item.detail ? `${item.label}, ${item.detail}` : undefined}
          className={item.danger ? 'danger' : undefined}
          onClick={() => {
            if (!item.more) onClose()
            item.run()
          }}
        >
          {item.icon ? <Icon name={item.icon} /> : null}
          {item.label}
          {item.detail ? <span className="sheet-detail">{item.detail}</span> : null}
          {item.more ? <Icon name="chevronRight" className="sheet-more" /> : null}
        </button>
      ))}
    </div>
  )
}
